import React, { useState } from "react";
import {
  Box,
  TextField,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Stack,
  Checkbox,
} from "@mui/material";

const PolicyForm = ({
  policyKey,
  policyDetails,
  setPolicyDetails,
  policyTypeOptions,
  nextQuarter,
  setNextQuarter,
  nextYear,
  setNextYear,
  setVersionFormatError,
}) => {
  const isUpdateMode = !!policyKey;

  const quarters = ["Q1", "Q2", "Q3", "Q4"];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - 2 + i); // 2 past, 4 future

  const [versionError, setVersionError] = useState("");

  const handleVersionChange = (e) => {
    const value = e.target.value;
    const regex = /^\d+\.\d+$/; // Matches number.number format like 1.0, 2.1

    if (!regex.test(value)) {
      setVersionError("Version must be in 'number.number' format, e.g., 1.0");
      setVersionFormatError(true);
    } else {
      setVersionError("");
      setVersionFormatError(false);
    }

    setPolicyDetails({ ...policyDetails, versionText: value });
  };

  return (
    <Box data-testid="policy-form">
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormLabel
            data-testid="policy-form-policy-number-label"
            required
            sx={{ width: 250 }}
          >
            Policy Number
          </FormLabel>
          <TextField
            data-testid="policy-form-policy-number-field"
            fullWidth
            disabled={isUpdateMode}
            value={policyDetails?.policyNumber}
            onChange={(e) =>
              setPolicyDetails({
                ...policyDetails,
                policyNumber: e.target.value,
              })
            }
          />
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormLabel
            data-testid="policy-form-policy-name-label"
            required
            sx={{ width: 250 }}
          >
            Policy Name
          </FormLabel>
          <TextField
            data-testid="policy-form-policy-name-field"
            fullWidth
            value={policyDetails?.policyTitle}
            onChange={(e) =>
              setPolicyDetails({
                ...policyDetails,
                policyTitle: e.target.value,
              })
            }
          />
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormLabel
            data-testid="policy-form-policy-version-label"
            required
            sx={{ width: 250 }}
          >
            Policy Version
          </FormLabel>
          <TextField
            data-testid="policy-form-policy-version-field"
            fullWidth
            disabled={isUpdateMode}
            value={policyDetails?.versionText}
            onChange={handleVersionChange}
            error={!!versionError}
            helperText={versionError}
          />
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <FormLabel
            data-testid="policy-form-policy-type-label"
            required
            sx={{ width: 250 }}
          >
            Policy Type
          </FormLabel>
          <RadioGroup
            data-testid="policy-form-policy-type-field"
            row
            value={(
              policyDetails?.policyTypeKey ?? "unknown".toUpperCase()
            ).toString()}
            onChange={(e) => {
              const { value } = e.target;
              const selectedKey =
                value === "unknown".toUpperCase() ? 6 : Number(value);
              const selectedOption = policyTypeOptions.find(
                (opt) => opt.policyTypeKey === selectedKey
              );
              setPolicyDetails({
                ...policyDetails,
                policyType: selectedOption ? selectedOption.policyType : "",
                policyTypeKey: selectedOption
                  ? selectedOption.policyTypeKey
                  : null,
              });
            }}
          >
            {policyTypeOptions.map((type) => (
              <FormControlLabel
                key={type.policyTypeKey}
                value={type.policyTypeKey.toString()}
                control={
                  <Radio
                    onChange={(e) => {
                      const { value } = e.target;
                      const selectedKey =
                        value === "UNKNOWN" ? 6 : Number(value);
                      const selectedOption = policyTypeOptions.find(
                        (opt) => opt.policyTypeKey === selectedKey
                      );
                      setPolicyDetails({
                        ...policyDetails,
                        policyType: selectedOption
                          ? selectedOption.policyType
                          : "",
                        policyTypeKey: selectedOption
                          ? selectedOption.policyTypeKey
                          : null,
                      });
                    }}
                  />
                }
                label={type.policyType}
              />
            ))}
          </RadioGroup>
        </Stack>
        {policyKey && (
          <Stack direction="row" spacing={2} alignItems="center">
            <FormLabel
              data-testid="policy-form-last-cab-cycle-label"
              sx={{ width: 250 }}
            >
              Last CAB Cycle
            </FormLabel>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ width: "100%" }}
            >
              <TextField
                data-testid="policy-form-last-cab-quarter-field"
                label="Quarter"
                fullWidth
                disabled={isUpdateMode}
                value={policyDetails?.quarter}
              />
              <TextField
                data-testid="policy-form-last-cab-year-field"
                label="Year"
                fullWidth
                disabled={isUpdateMode}
                value={policyDetails?.year}
              />
            </Stack>
          </Stack>
        )}

        <Stack direction="row" spacing={2}>
          <FormLabel
            data-testid="policy-form-next-cab-cycle-label"
            required
            sx={{ width: 250 }}
          >
            Next CAB Cycle
          </FormLabel>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ width: "100%" }}
          >
            <FormControl fullWidth>
              <InputLabel data-testid="policy-form-next-cab-cycle-quarter-label">
                Quarter
              </InputLabel>
              <Select
                data-testid="policy-form-next-cab-cycle-quarter-field"
                label="Quarter"
                value={nextQuarter}
                onChange={(e) => setNextQuarter(e.target.value)}
              >
                {quarters.map((q) => (
                  <MenuItem key={q} value={q}>
                    {q}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel data-testid="policy-form-next-cab-cycle-year-label">
                Year
              </InputLabel>
              <Select
                data-testid="policy-form-next-cab-cycle-year-field"
                label="Year"
                value={nextYear}
                onChange={(e) => setNextYear(e.target.value)}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
        {!policyKey && (
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              data-testid="policy-form-policy-details-label"
              control={
                <Checkbox
                  data-testid="policy-form-policy-details-field"
                  checked={!!policyDetails?.mapToHealthPlan}
                  onChange={(e) =>
                    setPolicyDetails({
                      ...policyDetails,
                      mapToHealthPlan: e.target.checked,
                    })
                  }
                  size="small"
                />
              }
              label="Map to Avalon - AHCS"
            />
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default PolicyForm;
